import React from 'react';
import { useQuery, NetworkStatus, usePaginationHelper } from '@common/hooks';
import { Link, EmptyContent, ErrorHandler } from '@common/components';
import { Table } from 'works/components/table';
import {
  PaginatedCandidateSubmissionsQuery,
  StaffingRequestApplicationsType,
} from '@applicant-flow/graphql/queries';
import { ApplicationStateBadge } from 'works/components/application-state-badge';
import { ActiveCtaButton } from 'works/pages/supplier/staffing-requests/staffing-request/application-list-cta-buttons/active-cta-button';
import { toUSFormat } from 'works/legacy/lib/date-helper';

const columns = [
  {
    title: 'ID',
    key: 'jobId',
    sorter: true,
    render: (
      _: unknown,
      { staffingRequest }: StaffingRequestApplicationsType
    ) => {
      return (
        <>
          <Link
            to={`/works/supplier/staffing_requests/${staffingRequest.id}/offers`}
            className="text-medium"
          >
            {staffingRequest.code}
          </Link>
        </>
      );
    },
  },
  {
    title: 'Name',
    dataIndex: ['staffingRequest', 'name'],
    key: 'jobName',
    sorter: true,
  },
  {
    title: 'Facility, City, State',
    dataIndex: 'id',
    key: 'facilityInfo',
    render: (
      _: unknown,
      { organization, staffingRequest }: StaffingRequestApplicationsType
    ) => {
      return (
        <>
          {organization.name}
          <br />
          <span className="secondary-paragraph">
            {staffingRequest.organizationBusiness.city},{' '}
            {staffingRequest.organizationBusiness.stateCode}
          </span>
        </>
      );
    },
    sorter: true,
  },
  {
    title: 'Unit',
    key: 'unitInfo',
    render: (
      _: unknown,
      { staffingRequest }: StaffingRequestApplicationsType
    ) => {
      return (
        <>
          {staffingRequest.organizationUnit.clinicalUnit.displayName}
          <br />
          <span className="secondary-paragraph">
            {staffingRequest.organizationBusiness.name}
          </span>
        </>
      );
    },
    sorter: true,
  },
  {
    title: 'Start Date',
    dataIndex: ['staffingRequest', 'startDate'],
    key: 'start',
    sorter: true,
    render: toUSFormat,
  },
  {
    title: 'End Date',
    dataIndex: ['staffingRequest', 'endDate'],
    key: 'end',
    sorter: true,
    render: toUSFormat,
  },
  {
    title: 'Status',
    key: 'statusInfo',
    render: (_: unknown, application: StaffingRequestApplicationsType) => (
      <ApplicationStateBadge
        state={application.state}
        timestamp={application.stateChangedAt}
        reason={application.rejectionReasonDescription}
        rejectionDetails={application.rejectionDetails}
        offerState={application.activeOffer?.state}
        offerTimestamp={application.activeOffer?.stateChangedAt}
        doNotRehire={application.doNotRehire}
      />
    ),
    sorter: true,
  },
  {
    key: 'buttonInfo',
    render: (_: unknown, application: StaffingRequestApplicationsType) => {
      return (
        <ActiveCtaButton
          staffingRequestId={application.staffingRequest.id}
          application={application}
        />
      );
    },
  },
];

export function CandidateSubmissionsList({
  worksProfileId,
}: {
  worksProfileId: string;
}) {
  const { paginationVariables, tableChangeHandler } = usePaginationHelper({
    localStorageFilterKey: 'CandidateSubmissionTableKey',
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'JOB_ID',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      jobId: 'JOB_ID',
      jobName: 'JOB_NAME',
      facilityInfo: 'FACILITY',
      unitInfo: 'NORMALIZED_UNIT',
      start: 'START',
      end: 'END',
      statusInfo: 'STATUS',
    },
  });

  const { error, data, networkStatus } = useQuery(
    PaginatedCandidateSubmissionsQuery,
    {
      variables: { ...paginationVariables, worksProfileId },
      notifyOnNetworkStatusChange: true,
    }
  );

  if (error) {
    return <ErrorHandler error={error} log="CandidateSubmissionsList" />;
  }

  const isLoaded = Boolean(networkStatus === NetworkStatus.ready && data);
  const applications = isLoaded
    ? data?.paginatedStaffingRequestApplications.staffingRequestApplications
    : [];

  if (isLoaded && applications?.length === 0)
    return (
      <EmptyContent
        imagePath="./no-templates.svg"
        header="Hmm..."
        text="There's nothing here."
      />
    );

  const total = isLoaded
    ? data?.paginatedStaffingRequestApplications.totalCount
    : null;

  return (
    <Table
      columns={columns}
      rowKey="jobId"
      dataSource={applications}
      loading={!isLoaded}
      pagination={{
        ...paginationVariables,
        total,
      }}
      onChange={tableChangeHandler}
    />
  );
}
